'use client';

import { useState } from 'react';
import { ProfileAvatar } from './ProfileAvatar';
import { Bio } from './Bio';
import { ProfileLink, ProfileStats } from './ProfileStats';
import { ProfileButtons } from './ProfileButtons';
import { IAchievement, IProfile } from 'types/src/types';
import { useProfileUIStore } from '../store/profile-ui-store';
import { cn } from '@workspace/ui/lib/utils';
import { useToast } from '@/src/components/ui';
import { useProfileMutations } from '@/src/hooks/use-profile';
import { useDonationGoal } from '@/src/hooks/use-donation-goal';
import { ProfileDonationGoal } from '../../finances/components/DonationGoal';
import { Donate } from './buttons/Donate';
import PageLoader from '@/src/components/ui/PageLoader';
import { PartnerCheckmark } from '../../settings/components/PartnerBadge';
import { Achievements } from '../../settings/components/Achievements';
import { IUIProfile } from '@/src/types/IUIProfile';

type Props = {
  profile: IProfile;
  isMyProfile: boolean;
  achievements: IAchievement[];
  currentUser: IUIProfile | null;
  loadingPage: boolean;
};

export const ProfileInfo = ({
  profile,
  isMyProfile,
  achievements,
  currentUser,
  loadingPage,
}: Props) => {
  const isMobileView = useProfileUIStore((s) => s.isMobileView);
  const previewMode = useProfileUIStore((s) => s.previewMode);
  const [isEditingName, setIsEditingName] = useState(false);
  const [editedName, setEditedName] = useState('');

  const toast = useToast();
  const { updateMutation } = useProfileMutations();

  const { data: goal, isLoading: isFetching } = useDonationGoal(
    profile?.id || -1,
  );

  const displayFullName =
    isMyProfile && currentUser?.fullName
      ? currentUser.fullName
      : profile.fullName || '';

  const handleStartEdit = () => {
    if (!isMyProfile || previewMode) return;
    setEditedName(displayFullName);
    setIsEditingName(true);
  };

  const handleCancelEdit = () => {
    setIsEditingName(false);
    setEditedName('');
  };

  const handleSaveName = async () => {
    const next = editedName.trim();
    if (!next || next === displayFullName) {
      handleCancelEdit();
      return;
    }

    await updateMutation.mutateAsync(
      { fullName: next },
      {
        onSuccess: () => {
          setIsEditingName(false);
          setEditedName('');
          toast.success('Name updated!');
        },
        onError: () => {
          toast.error('Failed to update name. Please try again.');
          setEditedName(displayFullName);
        },
      },
    );
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') handleSaveName();
    else if (e.key === 'Escape') handleCancelEdit();
  };

  return (
    <div
      className={cn('flex flex-col h-max', {
        'lg:col-span-4 md:pt-5 md:pl-0 ': !isMobileView,
      })}
    >
      {isFetching && <PageLoader />}

      <div
        className={cn('flex mb-2 w-full', {
          'lg:flex-col md:mb-5': !isMobileView,
        })}
      >
        <ProfileAvatar
          isMyProfile={isMyProfile}
          avatarUrl={profile.avatarUrl}
        />

        <div
          className={cn('flex flex-col items-start w-full ml-3', {
            'md:ml-0 md:mr-0': !isMobileView,
          })}
        >
          <ProfileLink uniqueLink={profile.uniqueLink} />

          {isEditingName ? (
            <div className="flex items-center gap-2 w-full">
              <input
                type="text"
                value={editedName}
                onChange={(e) => setEditedName(e.target.value)}
                onKeyDown={handleKeyDown}
                onBlur={handleSaveName}
                className={cn(
                  'font-semibold ml-2 text-black bg-transparent outline-none',
                  {
                    'xl:text-[40px] md:text-3xl md:m-3 md:mr-0 md:px-2':
                      !isMobileView,
                  },
                )}
                autoFocus
                disabled={updateMutation.isPending}
                maxLength={100}
              />
              {updateMutation.isPending && (
                <div className="animate-spin rounded-full h-4 w-4" />
              )}
              {profile.isPartner ? (
                <PartnerCheckmark className="inline-block ml-2 md:size-6 size-3.5" />
              ) : null}
            </div>
          ) : (
            <h1
              className={cn(
                'font-semibold my-2 text-black flex items-center justify-center',
                {
                  'xl:text-[40px]! md:text-3xl! md:my-3! md:ml-0 md:pl-2!':
                    !isMobileView,
                  'cursor-pointer rounded px-1 transition-colors':
                    isMyProfile && !previewMode,
                },
              )}
              onClick={handleStartEdit}
              title={
                isMyProfile && !previewMode ? 'Click to edit name' : undefined
              }
            >
              {displayFullName}
              {profile.isPartner ? (
                <PartnerCheckmark className="inline-block ml-2 md:size-6 size-3.5" />
              ) : null}
            </h1>
          )}

          <ProfileStats
            followers={profile.followersCount}
            subscribers={profile.subscribersCount}
            bits={profile.bitsCount ?? 0}
          />
        </div>
      </div>

      <div
        className={cn('flex flex-col-reverse', {
          'flex-col': !isMobileView,
        })}
      >
        <Bio
          isMyProfile={isMyProfile}
          initialBio={profile.bio}
          isMobileView={isMobileView}
        />

        {loadingPage || isFetching ? (
          <PageLoader />
        ) : (
          (!isMyProfile || previewMode) && (
            <ProfileButtons
              isMobileView={isMobileView}
              creatorProfile={profile}
              authenticatedUser={currentUser ?? null}
              isGoal={!!goal}
              isMyProfile={isMyProfile}
            />
          )
        )}

        {goal && (
          <ProfileDonationGoal goal={goal}>
            <Donate
              creatorId={profile.id as number}
              userId={currentUser?.id as number}
              stripeAccount={profile.stripeAccountId}
              creatorName={displayFullName || ''}
              profileUniqueLink={profile.uniqueLink}
              creatorImage={profile.avatarUrl || ''}
              isAnonymous={currentUser?.type === 'anonymous'}
              isMyProfile={isMyProfile}
              className={goal ? 'w-24' : ''}
              paymentProvider={profile.payment.provider}
              paypalStatus={profile.payment.paypalStatus}
            />
          </ProfileDonationGoal>
        )}

        <hr className="my-5 w-full border-t border-black/10" />
        <Achievements profileAchievements={achievements} />
      </div>
    </div>
  );
};
